import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "@cakrawalajakarta/shared-components";
// import { Country, State, City } from "country-state-city"; // Library removed
import { useNotification } from "../common/NotificationContext";

export const InputFieldVolunteer = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
}) => {
  const { showNotification } = useNotification();
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [rawCountries, setRawCountries] = useState([]);
  const [provinceIds, setProvinceIds] = useState({});
  const [countryMap, setCountryMap] = useState({});
  const [stateMap, setStateMap] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState({
    countries: false,
    provinces: false,
    cities: false,
  });

  const GEO_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/geo`;
  const cache = useMemo(() => new Map(), []);

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "ID Number";
    if (str === "motivasiDanPengalaman")
      return "Volunteer Motivation and Experience";
    if (str === "ukuranJersey") return "Jersey Size";
    if (str === "linkSertifikat") return "Certificate Link";
    if (str === "usernameInstagram") return "Instagram Username";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const fetchWithCache = async (url, cacheKey, type) => {
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.geonames) {
        cache.set(cacheKey, data);
        return data;
      } else {
        setErrors((prev) => ({
          ...prev,
          [type]: "Failed to load location data. Please try again later.",
        }));
        showNotification(
          "error",
          "Failed to load location data. Please try again later."
        );
        return null;
      }
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [type]: "Failed to load location data. Please try again later.",
      }));
      showNotification(
        "error",
        "Failed to load location data. Please try again later."
      );
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    // Only use GeoNames API
    fetchWithCache(
      `${GEO_API_BASE}/countries`,
      "countries",
      "countries"
    ).then((data) => {
      if (data?.geonames) {
        const countryList = data.geonames.map((country) => ({
          value: country.countryName,
          label: country.countryName,
        })).sort((a, b) => a.label.localeCompare(b.label));
        setCountries(countryList);
        setRawCountries(data.geonames);
      } else {
        setCountries([]);
        setRawCountries([]);
      }
    });
  }, [cache]);

  useEffect(() => {
    if (formData.negara) {
      setProvinces([]);
      setCities([]);

      // Use GeoNames API
      if (rawCountries.length > 0) {
        const selectedCountry = rawCountries.find(
          (c) => c.countryName === formData.negara
        );
        if (!selectedCountry) return;
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${selectedCountry.geonameId}?lang=en`,
          `provinces_${selectedCountry.geonameId}`,
          "provinces"
        ).then((data) => {
          if (data?.geonames) {
            const provinceList = data.geonames.map((prov) => ({
              value: prov.name,
              label: prov.name,
            })).sort((a, b) => a.label.localeCompare(b.label));
            const idMap = {};
            data.geonames.forEach((prov) => {
              idMap[prov.name] = prov.geonameId;
            });
            setProvinces(provinceList);
            setProvinceIds(idMap);
          }
        });
      }
    }
  }, [formData.negara, rawCountries, cache]);

  useEffect(() => {
    if (formData.provinsi && formData.negara) {
      setCities([]);

      // Use GeoNames API
      if (provinceIds[formData.provinsi]) {
        const provinceId = provinceIds[formData.provinsi];
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${provinceId}?lang=en`,
          `cities_${provinceId}`,
          "cities"
        ).then((data) => {
          if (data?.geonames) {
            const cityList = data.geonames.map((city) => ({
              value: city.name,
              label: city.name,
            })).sort((a, b) => a.label.localeCompare(b.label));
            setCities(cityList);
          }
        });
      }
    }
  }, [formData.provinsi, formData.negara, provinceIds, cache]);

  const validateField = (name, value, negara = "Indonesia") => {
    const label = humanize(name);

    if (
      name !== "linkSertifikat" &&
      name !== "usernameInstagram" &&
      (value === undefined || value === null || value === "")
    ) {
      return `${label} is required`;
    }
    if (name === "namaDepan" && !/^[A-Za-z\s]{1,50}$/.test(value)) {
      return `${label} must only contain letters and spaces, max 50 characters, e.g., "John"`;
    }
    if (name === "namaBelakang" && !/^[A-Za-z\s]{1,50}$/.test(value)) {
      return `${label} must only contain letters and spaces, max 50 characters, e.g., "Doe"`;
    }
    if (name === "telepon" && !/^\+?\d{8,15}$/.test(value)) {
      return `${label} must be 8-15 digits, can start with "+", e.g., "+6281234567890"`;
    }
    if (name === "nomorIdentitas") {
      if (negara === "Indonesia") {
        if (!/^\d{16}$/.test(value)) {
          return `${label} (KTP) must be 16 digits for Indonesian participants, e.g., "1234567890123456"`;
        }
      } else {
        if (!/^[A-Za-z0-9]{5,20}$/.test(value)) {
          return `${label} (Passport/ID) must be 5-20 alphanumeric characters for foreign participants, e.g., "AB123456"`;
        }
      }
    }
    if (
      name === "linkSertifikat" &&
      value &&
      !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(value)
    ) {
      return `${label} must be a valid URL, e.g., "https://yourcertificatelink"`;
    }
    if (
      name === "usernameInstagram" &&
      value &&
      !/^[A-Za-z0-9._]{1,30}$/.test(value)
    ) {
      return `${label} must only contain letters, numbers, dots, or underscores, max 30 characters, e.g., "user_name"`;
    }
    if (name === "tanggalLahir") {
      const date = new Date(value);
      const eventDate = new Date("2025-09-27");
      const minAgeDate = new Date(
        eventDate.getFullYear() - 18,
        eventDate.getMonth(),
        eventDate.getDate()
      );
      if (isNaN(date.getTime()) || date >= eventDate) {
        return `${label} must be in the past before 27/09/2025, e.g., "2005-09-27"`;
      }
      if (date > minAgeDate) {
        return `Volunteer must be at least 18 years old on 27/09/2025, e.g., "2007-09-27" or earlier`;
      }
    }
    if (name === "gender" && !["Male", "Female"].includes(value)) {
      return `${label} must be Male or Female`;
    }
    if (
      name === "negara" &&
      countries.length > 0 &&
      !countries.some((c) => c.value === value)
    ) {
      return `${label} must be selected from the available list, e.g., "Indonesia"`;
    }
    if (
      name === "provinsi" &&
      provinces.length > 0 &&
      !provinces.some((p) => p.value === value)
    ) {
      return `${label} must be selected from the available list, e.g., "West Java"`;
    }
    if (
      name === "kota" &&
      cities.length > 0 &&
      !cities.some((c) => c.value === value)
    ) {
      return `${label} must be selected from the available list, e.g., "Jakarta"`;
    }
    if (name === "alamat" && value && value.trim().length < 5) {
      return `${label} is too short`;
    }
    if (
      name === "motivasiDanPengalaman" &&
      !/^[A-Za-z0-9\s,.!?-]{10,500}$/.test(value)
    ) {
      return `${label} must be 10-500 characters, alphanumeric, spaces, and certain punctuation marks, e.g., "I have been a volunteer in a running race and want to help at the aid station."`;
    }
    if (
      name === "ukuranJersey" &&
      !["XS", "S", "M", "L", "XL", "XXL"].includes(value)
    ) {
      return `${label} must be selected from the available size list, e.g., "M"`;
    }
    if (
      name === "posisiVolunteer" &&
      ![
        "Marshalls",
        "Water Station Crew",
        "Racepack Collection Crew",
        "Race Venue Crew",
        "Medis & Rescue",
        "Photographer",
        "Check Point",
        "Logistik",
      ].includes(value)
    ) {
      return `${label} must be selected from the available positions list, e.g., "Water Station Crew"`;
    }
    return "";
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    handleChange(e);
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value, formData.negara);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {errors.countries && (
        <p className="text-red-500 col-span-full">{errors.countries}</p>
      )}
      <InputField
        label="First Name"
        name="namaDepan"
        value={formData.namaDepan || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.namaDepan && errors.namaDepan}
        labelColor={labelColor}
      />
      <InputField
        label="Last Name"
        name="namaBelakang"
        value={formData.namaBelakang || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.namaBelakang && errors.namaBelakang}
        labelColor={labelColor}
      />
      <InputField
        label="Phone/WhatsApp Number"
        name="telepon"
        value={formData.telepon || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.telepon && errors.telepon}
        labelColor={labelColor}
      />
      <InputField
        label="ID Number"
        name="nomorIdentitas"
        value={formData.nomorIdentitas || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.nomorIdentitas && errors.nomorIdentitas}
        labelColor={labelColor}
        placeholder={
          formData.negara === "Indonesia"
            ? "Enter 16 digit KTP"
            : "Enter Passport/ID number"
        }
      />
      <InputField
        label="Certificate Link"
        name="linkSertifikat"
        value={formData.linkSertifikat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required={false}
        error={touched.linkSertifikat && errors.linkSertifikat}
        labelColor={labelColor}
        placeholder="Certificate link or other compressed supporting documents"
      />
      <InputField
        label="Instagram Username"
        name="usernameInstagram"
        value={formData.usernameInstagram || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required={false}
        error={touched.usernameInstagram && errors.usernameInstagram}
        labelColor={labelColor}
        placeholder="Enter username, e.g., user_name"
      />
      <InputField
        label="Date of Birth"
        name="tanggalLahir"
        type="date"
        value={formData.tanggalLahir || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.tanggalLahir && errors.tanggalLahir}
        labelColor={labelColor}
      />
      <InputField
        label="Gender"
        name="gender"
        type="select"
        value={formData.gender || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        options={[
          { value: "", label: "Select Gender" },
          { value: "Male", label: "Male" },
          { value: "Female", label: "Female" },
        ]}
        error={touched.gender && errors.gender}
        labelColor={labelColor}
      />
      <InputField
        label="Country"
        name="negara"
        type="select"
        value={formData.negara || "Indonesia"}
        onChange={handleFieldChange}
        disabled={isFormDisabled || loading.countries}
        options={countries}
        required
        error={touched.negara && errors.negara}
        aria-busy={loading.countries}
        labelColor={labelColor}
      />
      <InputField
        label="Province"
        name="provinsi"
        type="select"
        value={formData.provinsi || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.negara || loading.provinces}
        options={[{ value: "", label: "Select Province" }, ...provinces]}
        required
        error={touched.provinsi && errors.provinsi}
        aria-busy={loading.provinces}
        labelColor={labelColor}
      />
      <InputField
        label="City"
        name="kota"
        type="select"
        value={formData.kota || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.provinsi || loading.cities}
        options={[{ value: "", label: "Select City" }, ...cities]}
        required
        error={touched.kota && errors.kota}
        aria-busy={loading.cities}
        labelColor={labelColor}
      />
      <InputField
        label="Address"
        name="alamat"
        value={formData.alamat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        error={touched.alamat && errors.alamat}
        labelColor={labelColor}
      />
      <InputField
        label="Jersey Size"
        name="ukuranJersey"
        type="select"
        value={formData.ukuranJersey || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        options={[
          { value: "", label: "Select Jersey Size" },
          { value: "XS", label: "XS" },
          { value: "S", label: "S" },
          { value: "M", label: "M" },
          { value: "L", label: "L" },
          { value: "XL", label: "XL" },
          { value: "XXL", label: "XXL" },
        ]}
        error={touched.ukuranJersey && errors.ukuranJersey}
        labelColor={labelColor}
      />
      <InputField
        label="Volunteer Position"
        name="posisiVolunteer"
        type="select"
        value={formData.posisiVolunteer || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        required
        options={[
          { value: "", label: "Select Volunteer Position" },
          { value: "Marshalls", label: "Marshalls" },
          { value: "Water Station Crew", label: "Water Station Crew" },
          {
            value: "Racepack Collection Crew",
            label: "Racepack Collection Crew",
          },
          { value: "Race Venue Crew", label: "Race Venue Crew" },
          { value: "Medis & Rescue", label: "Medis & Rescue" },
          { value: "Photographer", label: "Photographer" },
          { value: "Check Point", label: "Check Point" },
          { value: "Logistik", label: "Logistik" },
        ]}
        error={touched.posisiVolunteer && errors.posisiVolunteer}
        labelColor={labelColor}
      />
      <div className="col-span-full">
        <InputField
          label="Volunteer Motivation and Experience"
          name="motivasiDanPengalaman"
          type="textarea"
          value={formData.motivasiDanPengalaman || ""}
          onChange={handleFieldChange}
          disabled={isFormDisabled}
          required
          error={touched.motivasiDanPengalaman && errors.motivasiDanPengalaman}
          labelColor={labelColor}
          placeholder="Tell us about your previous volunteer experience and your motivation to join!"
          className="col-span-full min-h-[240px] w-full max-w-full resize-y !important"
          style={{ minHeight: "240px" }}
        />
      </div>
    </div>
  );
};
